import React, { useState } from 'react';
import dayjs from 'dayjs';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import styles from './stats.module.css';
import palette from './palette';
import ChartTooltip from './ChartTooltip';
import useElementWidth from './useElementWidth';
import { useStatsPrint } from './printContext';
import { useStatsCompare } from './compareContext';
import { formatBucket, formatMoney, formatMoneyShort, formatNumber } from '../utils/format';

const AXIS_TICK = { fontSize: 11, fill: palette.text };
const COMPARE = '__compare';
const COMPARE_OPACITY = 0.32;

/** Balde com o ano, para o balão não confundir "11/10" de dois anos diferentes. */
const fullBucket = (bucket, granularity) => (
  granularity === 'month' ? formatBucket(bucket, granularity) : dayjs(bucket).format('DD/MM/YY')
);

const sumOf = (row, keys) => {
  const values = keys.map((key) => row[`${key}Prev`]).filter((value) => value !== null && value !== undefined);
  return values.length > 0 ? values.reduce((sum, value) => sum + Number(value), 0) : null;
};

/** Item da legenda com caixinha de marcar: deixa claro que clicar esconde ou mostra a série. */
const LegendToggle = ({ label, color, off, faded = false, onClick }) => (
  <button
    type="button"
    className={`${styles.legendItem} ${off ? styles.legendItemOff : ''}`}
    onClick={onClick}
    aria-pressed={!off}
    title={off ? `Mostrar ${label} no gráfico` : `Esconder ${label} do gráfico`}
  >
    <span className={styles.legendCheck} style={{ background: color, borderColor: color, opacity: faded ? 0.55 : 1 }}>
      <CheckRoundedIcon />
    </span>
    {label}
  </button>
);

/**
 * Evolução no período (por dia, semana ou mês, conforme o backend agrupou).
 *
 * `data`: [{ bucket, <campo>: número, bucketPrev?, <campo>Prev? }]
 * `series`: [{ key, label, color, stack?, hidden? }] (`hidden`: começa desmarcada)
 * `kind`: 'bar' (padrão, cantos arredondados) ou 'line' (área com degradê);
 * `money`: valores em reais. Cada item da legenda tem uma caixinha de marcar
 * que esconde ou mostra a série.
 * `total`: { label, keys } soma de algumas séries no fim do balão (ex.: "Previsto" =
 * realizados + ainda por vir), para a pessoa não precisar somar de cabeça.
 * `goal`: meta mensal; vira uma linha tracejada "Meta" quando o gráfico está
 * agrupado por mês (por dia ou semana a meta do mês não se aplica).
 *
 * Quando a API manda o período comparado (`<campo>Prev`), ele aparece ao lado:
 * em barras, uma barra clara à esquerda de cada barra ou pilha (pilha vira o
 * total); em linhas, uma linha tracejada. A legenda tem um item para escondê-lo.
 */
const TimelineChart = ({ data, series, granularity, kind = 'bar', money = false, height = 220, animate = true, goal = null, total = null }) => {
  const print = useStatsPrint();
  const compare = useStatsCompare();
  const [ref, width] = useElementWidth();
  const [hidden, setHidden] = useState(() => series.filter((item) => item.hidden).map((item) => item.key));

  const points = data || [];
  const canCompare = compare.enabled && points.some((point) => point.bucketPrev);
  const hasData = points.some((point) => series.some((item) => Number(point[item.key]) || Number(point[`${item.key}Prev`])));
  if (!hasData) return <div className={styles.emptyChart}>Sem movimento no período.</div>;

  const format = money ? formatMoney : (value) => formatNumber(value, 1);
  const visible = series.filter((item) => !hidden.includes(item.key));
  const showCompare = canCompare && !hidden.includes(COMPARE);
  const animated = animate && !print;
  const showGoal = goal > 0 && granularity === 'month';

  // Barras: um grupo por pilha (ou por série solta), cada um com a sua barra comparada
  const groups = [];
  series.forEach((item) => {
    const id = item.stack || item.key;
    const group = groups.find((entry) => entry.id === id);
    if (group) group.members.push(item);
    else groups.push({ id, members: [item] });
  });
  const compareName = (members) => `${members.length > 1 ? 'Total' : members[0].label} ${compare.suffix}`;

  const rows = points.map((point) => {
    const row = { ...point, label: formatBucket(point.bucket, granularity) };
    groups.forEach((group) => {
      const keys = group.members.filter((item) => !hidden.includes(item.key)).map((item) => item.key);
      row[`${COMPARE}${group.id}`] = point.bucketPrev ? sumOf(point, keys) : null;
    });
    return row;
  });

  // Em barras empilhadas, só a de cima de cada pilha leva o canto arredondado
  const topOfStack = {};
  visible.forEach((item) => { topOfStack[item.stack || item.key] = item.key; });

  const toggle = (key) => setHidden((current) => (
    current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
  ));

  const tooltipTitle = (label, payload) => {
    const point = payload && payload[0] && payload[0].payload;
    if (!showCompare || !point || !point.bucketPrev) return label;
    return `${fullBucket(point.bucket, granularity)} vs ${fullBucket(point.bucketPrev, granularity)}`;
  };
  const tooltipFooter = total ? (payload) => {
    const point = payload && payload[0] && payload[0].payload;
    if (!point) return null;
    return { label: total.label, value: format(total.keys.reduce((sum, key) => sum + (Number(point[key]) || 0), 0)) };
  } : undefined;
  const isCompareEntry = (entry) => String(entry.dataKey).startsWith(COMPARE) || String(entry.dataKey).endsWith('Prev');

  const renderBars = () => groups.flatMap((group) => {
    const members = group.members.filter((item) => !hidden.includes(item.key));
    if (members.length === 0) return [];
    const bars = [];
    if (showCompare) {
      bars.push(
        <Bar
          key={`${COMPARE}${group.id}`}
          dataKey={`${COMPARE}${group.id}`}
          name={compareName(group.members)}
          fill={members[0].color}
          fillOpacity={COMPARE_OPACITY}
          stackId={`${COMPARE}${group.id}`}
          maxBarSize={28}
          radius={[6, 6, 0, 0]}
          isAnimationActive={animated}
        />
      );
    }
    members.forEach((item) => bars.push(
      <Bar
        key={item.key}
        dataKey={item.key}
        name={item.label}
        fill={item.color}
        stackId={item.stack}
        maxBarSize={showCompare ? 28 : 36}
        radius={topOfStack[item.stack || item.key] === item.key ? [6, 6, 0, 0] : 0}
        isAnimationActive={animated}
      />
    ));
    return bars;
  });

  const renderLines = () => visible.flatMap((item) => [
    showCompare && (
      <Line
        key={`${item.key}Prev`}
        type="monotone"
        dataKey={`${item.key}Prev`}
        name={`${item.label} ${compare.suffix}`}
        stroke={item.color}
        strokeOpacity={0.45}
        strokeWidth={2}
        strokeDasharray="5 5"
        dot={false}
        activeDot={{ r: 4, strokeWidth: 0, fillOpacity: 0.5 }}
        connectNulls
        isAnimationActive={animated}
      />
    ),
    <Area
      key={item.key}
      type="monotone"
      dataKey={item.key}
      name={item.label}
      stroke={item.color}
      strokeWidth={2.5}
      fill={`url(#stats-fill-${item.key})`}
      dot={rows.length <= 16 ? { r: 3, strokeWidth: 2, fill: '#FFFFFF' } : false}
      activeDot={{ r: 5, strokeWidth: 0 }}
      connectNulls
      isAnimationActive={animated}
    />,
  ].filter(Boolean));

  return (
    <div>
      <div className={styles.legend}>
        {series.map((item) => (
          <LegendToggle
            key={item.key}
            label={item.label}
            color={item.color}
            off={hidden.includes(item.key)}
            onClick={() => toggle(item.key)}
          />
        ))}
        {canCompare && (
          <LegendToggle
            label={compare.short}
            color={series[0].color}
            faded
            off={hidden.includes(COMPARE)}
            onClick={() => toggle(COMPARE)}
          />
        )}
      </div>
      <div ref={ref} className={styles.chartArea} style={{ height }}>
        {width > 0 && (
          <ComposedChart width={width} height={height} data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
            <defs>
              {series.map((item) => (
                <linearGradient key={item.key} id={`stats-fill-${item.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={item.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={item.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke={palette.grid} />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: palette.grid }} minTickGap={8} />
            <YAxis
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={money ? 64 : 36}
              allowDecimals={false}
              tickFormatter={money ? formatMoneyShort : (value) => formatNumber(value)}
            />
            <Tooltip
              cursor={kind === 'line' ? { stroke: palette.blueSoft, strokeDasharray: '4 4' } : { fill: '#F3F6FE' }}
              content={<ChartTooltip format={(value) => format(value)} title={tooltipTitle} dim={isCompareEntry} footer={tooltipFooter} />}
            />
            {kind === 'line' ? renderLines() : renderBars()}
            {showGoal && (
              <ReferenceLine
                y={goal}
                ifOverflow="extendDomain"
                stroke={palette.text}
                strokeDasharray="4 4"
                label={{ value: `Meta ${formatNumber(goal)}`, position: 'insideTopRight', fontSize: 11, fill: palette.text }}
              />
            )}
          </ComposedChart>
        )}
      </div>
    </div>
  );
};

export default TimelineChart;
